import { ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppShell,
  Burger,
  Group,
  Text,
  Button,
  Stack,
  UnstyledButton,
} from "@mantine/core";
import { useMobileNavbar } from "../../../hooks/useMobileNavbar";
import { IconGavel, IconFileText, IconBuilding, IconLogout, IconShield, IconClipboardList, IconMail } from "@tabler/icons-react";
import { useVendorAuth } from "../auth/VendorAuthContext";
import { notifications } from "@mantine/notifications";

interface VendorLayoutProps {
  children: ReactNode;
}

export function VendorLayout({ children }: VendorLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { vendor, logout } = useVendorAuth();
  const { opened, toggle, close, navbar } = useMobileNavbar();

  const handleLogout = () => {
    logout();
    notifications.show({
      title: "Logged out",
      message: "You have been successfully logged out.",
      color: "green",
    });
    navigate("/vendor/login");
  };

  const navItems = [
    {
      label: "Dashboard",
      icon: IconShield,
      path: "/vendor/dashboard",
      color: "green",
    },
    {
      label: "Tenders",
      icon: IconGavel,
      path: "/vendor/tenders",
      color: "green",
    },
    {
      label: "RFQ",
      icon: IconMail,
      path: "/vendor/rfq",
      color: "green",
    },
    {
      label: "Documents",
      icon: IconFileText,
      path: "/vendor/documents",
      color: "green",
    },
    {
      label: "My Submissions",
      icon: IconClipboardList,
      path: "/vendor/submissions",
      color: "green",
    },
    {
      label: "Profile",
      icon: IconBuilding,
      path: "/vendor/profile",
      color: "green",
    },
  ];

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={navbar}
      padding="md"
    >
      <AppShell.Header>
        <Group justify="space-between" h="100%" px="md">
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <IconShield size={32} className="text-green-600" />
            <Text size="lg" fw={700}>
              Vendor Portal
            </Text>
          </Group>
          <Group>
            <Text size="sm" c="dimmed">
              {vendor?.businessName || vendor?.email || "Vendor"}
            </Text>
            <Button
              variant="subtle"
              size="sm"
              leftSection={<IconLogout size={16} />}
              onClick={handleLogout}
              color="green"
            >
              Logout
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <Stack gap="md">
          {navItems.map((item) => (
            <UnstyledButton
              key={item.path}
              className={`p-3 rounded-md transition-colors ${
                isActive(item.path)
                  ? "bg-green-50 text-green-700"
                  : "hover:bg-gray-100"
              }`}
              onClick={() => {
                navigate(item.path);
                close();
              }}
            >
              <Group>
                <item.icon
                  size={20}
                  className={isActive(item.path) ? "text-green-600" : "text-gray-600"}
                />
                <Text fw={isActive(item.path) ? 600 : 400}>{item.label}</Text>
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  );
}

